import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supportChatApi as api } from '../../services/supportChatApi';
import useChatPolling from './useChatPolling';
import { moduleNames } from './observationProtocol';
import LivePageViewer from './LivePageViewer';
import UsageMetrics from './UsageMetrics';
import './observation.css';
const base = '/api/v1/support-chat';

export function OnlineUsers({ onSelect }) {
  const { t } = useTranslation('supportChat');
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useChatPolling(async signal => {
    try {
      const { data } = await api.get(`${base}/online`, { signal });
      if (!signal.aborted) { setUsers(data.users); setError(''); }
    } catch (_) { if (!signal.aborted) setError(t('liveSupport.loadErrorGeneric')); }
  }, 10000);
  const attend = async id => {
    setLoading(true);
    try {
      const { data } = await api.post(`${base}/online/${id}/conversation`);
      if (alive.current) { onSelect(data.conversationId); setError(''); }
    } catch (err) { if (alive.current) setError(t('liveSupport.attendError')); }
    finally { if (alive.current) setLoading(false); }
  };
  return <details className="eo-online-users" open><summary><span className="eo-live-dot" /> {t('liveSupport.onlineUsersSummary', { count: users.length })}</summary>
    <div className="eo-online-list">{users.map(user => <button type="button" key={user._id} disabled={loading} onClick={() => attend(user._id)}><strong>{user.restaurant}</strong><span>{user.name} · {t(`moduleNames.${user.module}`, moduleNames[user.module])}</span></button>)}</div>
    {!users.length && <p>{t('liveSupport.noUsersOnline')}</p>}{error && <p role="alert">{error}</p>}
  </details>;
}

export default function LiveSupport({ conversationId }) {
  const { t } = useTranslation('supportChat');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [tabId, setTabId] = useState('');
  const [followActive, setFollowActive] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const session = useRef(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (session.current) api.post(`${base}/live/${session.current}/stop`).catch(() => {});
    };
  }, []);
  const load = async signal => {
    try {
      const { data: response } = await api.get(`${base}/conversations/${conversationId}/live`, { signal });
      if (signal?.aborted || !alive.current) return;
      session.current = response.session?._id || null;
      setData(response);
      if (response.session) { setTabId(response.session.tabId); setFollowActive(response.session.followActive !== false); }
      setError('');
    } catch (err) { if (!signal?.aborted && alive.current) setError(t('liveSupport.loadErrorGeneric')); }
  };
  useChatPolling(load, 5000, expanded);
  const start = async () => {
    setBusy(true); setError('');
    try {
      const { data: response } = await api.post(`${base}/conversations/${conversationId}/live`, { tabId: tabId || data.tabs[0].tabId, followActive });
      if (!alive.current) { api.post(`${base}/live/${response.session._id}/stop`).catch(() => {}); return; }
      session.current = response.session._id;
      await load();
    } catch (err) { if (alive.current) setError(t('liveSupport.startError')); }
    finally { if (alive.current) setBusy(false); }
  };
  const stop = async () => {
    const id = session.current;
    if (!id) return;
    setBusy(true);
    try {
      await api.post(`${base}/live/${id}/stop`);
      if (alive.current) { session.current = null; await load(); }
    } catch (_) { if (alive.current) setError(t('liveSupport.endError')); }
    finally { if (alive.current) setBusy(false); }
  };
  const changeTab = async (nextTab, follow) => {
    setTabId(nextTab); setFollowActive(follow);
    if (!session.current) return;
    setBusy(true);
    try { await api.patch(`${base}/live/${session.current}/tab`, { tabId: nextTab, followActive: follow }); await load(); }
    catch (err) { setError(t('liveSupport.changeTabError')); }
    finally { if (alive.current) setBusy(false); }
  };
  const toggle = event => {
    const open = event.currentTarget.open;
    setExpanded(open);
    if (!open && session.current) stop();
  };
  return <div className="eo-live-support">
    <details onToggle={toggle}><summary>{t('liveSupport.sectionTitle')}</summary>
      <div className="eo-live-content">
        <p>{t('liveSupport.description')}</p>
        {!data && !error && <p>{t('liveSupport.checkingAvailability')}</p>}
        {error && <p className="eo-observation-error" role="alert">{error}</p>}
        {data && <><div className="eo-live-actions">
          <select aria-label={t('liveSupport.tabSelectAria')} value={tabId || data.session?.tabId || data.tabs[0]?.tabId || ''}
            onChange={event => changeTab(event.target.value, false)} disabled={busy || !data.tabs.length}>
            {!data.tabs.length && <option value="">{t('liveSupport.waitingForTab')}</option>}
            {data.tabs.map((tab, index) => <option key={tab.tabId} value={tab.tabId}>{t('liveSupport.tabOption', { index: index + 1, module: t(`moduleNames.${tab.module}`, moduleNames[tab.module] || t('liveSupport.otherModule')) })}</option>)}
          </select>
          <label><input type="checkbox" checked={followActive} disabled={busy || !data.tabs.length}
            onChange={event => changeTab(tabId || data.tabs[0]?.tabId, event.target.checked)} /> {t('liveSupport.followActive')}</label>
          {data.session ? <button type="button" disabled={busy} onClick={stop}>{t('liveSupport.endSession')}</button>
            : <button type="button" disabled={busy || !data.allowed || data.busy || !data.tabs.length} onClick={start}>{t('liveSupport.startSession')}</button>}
          <span>{data.busy ? t('liveSupport.otherAgentWatching') : data.allowed ? t('liveSupport.priorAuthorizationActive') : t('liveSupport.waitingAuthorization')}</span>
        </div>
          {data.session && <LivePageViewer key={data.session._id} sessionId={data.session._id} />}
          {data.session && <small>{t('liveSupport.streamingNote')}</small>}
        </>}
      </div>
    </details>
    <UsageMetrics conversationId={conversationId} />
  </div>;
}
